import { Button } from "components/ui/Button";
import { BULK_RESEND_MAX_SERIES } from "hooks/use-delivery-series-api";
import { RefreshCw } from "lucide-react";
import { type FC, useEffect, useRef } from "react";

/** 一覧の行とバーの全選択で見た目を揃えるチェックボックスのクラス */
export const SERIES_CHECKBOX_CLASS = "checkbox checkbox-sm";

const TOGGLE_ALL_LABEL = "表示中のシリーズをまとめて選択";
const MAX_NOTE = `最大 ${BULK_RESEND_MAX_SERIES} 件まで`;
const RESEND_LABEL = "まとめて配信";
const CLEAR_LABEL = "選択解除";

export type SeriesBatchActionBarProps = {
  /** 選んでいるシリーズの件数 */
  checkedCount: number;
  /** 全選択で選ぶ件数 (= min(上限, 選べる行の件数)) */
  selectAllCap: number;
  onToggleAll: () => void;
  onResend: () => void;
  onClearSelection: () => void;
};

/**
 * シリーズ一覧の一括操作バー (StatusTabs の直後、リストのカードの外).
 * 0 件のときも表示し、操作できないボタンは無効にする.
 * @param {SeriesBatchActionBarProps} props 選択件数とハンドラ
 * @return {JSX.Element} バー
 */
export const SeriesBatchActionBar: FC<SeriesBatchActionBarProps> = ({
  checkedCount,
  selectAllCap,
  onToggleAll,
  onResend,
  onClearSelection,
}) => {
  const toggleAllRef = useRef<HTMLInputElement>(null);
  const allChecked = selectAllCap > 0 && checkedCount === selectAllCap;
  const partlyChecked = checkedCount > 0 && checkedCount < selectAllCap;
  const empty = checkedCount === 0;

  // indeterminate は属性では指定できないので DOM に直接設定する
  useEffect(() => {
    if (toggleAllRef.current) {
      toggleAllRef.current.indeterminate = partlyChecked;
    }
  }, [partlyChecked]);

  return (
    <div
      className="sticky top-0 z-10 flex min-h-9 flex-wrap items-center gap-2 rounded-md border border-violet-200 bg-violet-50 px-4 py-1"
      data-testid="series-batch-bar"
    >
      <label className="flex cursor-pointer items-center gap-3 text-xs">
        <input
          ref={toggleAllRef}
          type="checkbox"
          className={SERIES_CHECKBOX_CLASS}
          checked={allChecked}
          disabled={selectAllCap === 0}
          onChange={onToggleAll}
          aria-label={TOGGLE_ALL_LABEL}
        />
        <span className="font-medium text-base-content/80">
          {checkedCount} 件選択中
        </span>
      </label>
      <span className="text-xs text-base-content/50">{MAX_NOTE}</span>
      <Button
        type="button"
        size="xs"
        variant="outline"
        onClick={onResend}
        disabled={empty}
      >
        <span className="inline-flex items-center gap-1">
          <RefreshCw size={12} aria-hidden />
          {RESEND_LABEL}
        </span>
      </Button>
      <button
        type="button"
        onClick={onClearSelection}
        disabled={empty}
        className="ml-auto text-xs text-base-content/60 hover:text-base-content disabled:cursor-not-allowed disabled:opacity-40"
      >
        {CLEAR_LABEL}
      </button>
    </div>
  );
};
